import { useLDClient, useLDReady } from 'launchdarkly-vue-client-sdk'
import type { LDClient, LDContext } from 'launchdarkly-vue-client-sdk'
import type { Ref } from 'vue'
import { ref } from 'vue'
import type { ProviderInterface } from '../ProviderInterface'
import { Context } from '../Context'
import { Flag } from '../Flag'

let globalClient: LDClient | null = null
let isClientReady = false

export class LaunchDarkly implements ProviderInterface {
  private readonly INIT_TIMEOUT = 5000
  private readonly INIT_CHECK_INTERVAL = 100

  private context: Context = new Context([])
  private initialized: Ref<boolean> = ref(false)

  constructor(private readonly contextKind: Readonly<string>) {}

  static startup() {
    try {
      const client = useLDClient()
      globalClient = client
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('LaunchDarkly: failed to initialize client', e)
    }
  }

  init() {
    try {
      this.initialized.value = false
      const context: LDContext = {
        kind: this.contextKind,
        key: this.context.stringify(),
        ...this.context.asObject(),
      }

      if (globalClient) {
        globalClient.identify(context, undefined, () => {
          this.initialized.value = true
          isClientReady = true
        })
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('LaunchDarkly: init failed', e)
    }
  }

  isEnabled(flag: Flag, context: Context): Ref<boolean> {
    if (context) {
      this.changeContext(context)
    }

    if (globalClient && isClientReady) {
      const value = globalClient.variation(flag, false)
      return ref(value)
    }

    return ref(false)
  }

  async waitForInit(): Promise<void> {
    try {
      const ldReady = useLDReady()
      const startTime = Date.now()

      while (!ldReady.value && Date.now() - startTime < this.INIT_TIMEOUT) {
        await new Promise((resolve) => setTimeout(resolve, this.INIT_CHECK_INTERVAL))
      }

      if (ldReady.value) {
        this.init()

        await new Promise<void>((resolve) => {
          const checkInterval = setInterval(() => {
            if (this.initialized.value) {
              clearInterval(checkInterval)
              resolve()
            }
          }, this.INIT_CHECK_INTERVAL)

          setTimeout(() => {
            clearInterval(checkInterval)
            resolve()
          }, this.INIT_TIMEOUT)
        })
      }
    } catch {
      // eslint-disable-next-line no-console
      console.error('LaunchDarkly: waitForInit failed')
    }
  }

  changeContext(context: Context): void {
    if (!this.contextDiffers(context)) {
      return
    }

    this.context = context
    this.init()
  }

  contextDiffers(context: Context): boolean {
    return this.context.stringify() !== context.stringify()
  }
}
